import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endOfMonth, endOfQuarter, endOfYear, formatISO, startOfMonth, startOfQuarter, startOfYear, subMonths, subQuarters, subYears } from "date-fns";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";
import { Calculator, ChevronLeft } from "lucide-react";
import { useAuth } from "../../utils/auth";
import { canViewFinancials } from "../../utils/roles";
import { ChartBox, EmptyState, LoadingBlock, SectionCard } from "../Analytics/AnalyticsUi";
import {
  fetchE7seblyDrilldown,
  fetchE7seblyPreview,
  fetchE7seblyReport,
  fetchE7seblyReports,
  fetchE7seblySettings,
  reviseE7seblyReport,
  saveE7seblyAdSpend,
  saveE7seblyConsumable,
  saveE7seblyExpense,
  saveE7seblyProductCost,
  saveE7seblyReport,
  saveE7seblyReturnRate,
  saveE7seblyShippingCost,
  saveE7seblySubsidy,
} from "../../api/E7seblyAPIs";
import { certaintyClass, certaintyLabel, formatCount, formatMoney, formatPercent } from "./e7seblyFormat";

const PRESETS = [
  { id: "thisMonth", label: "هذا الشهر" },
  { id: "lastMonth", label: "الشهر الماضي" },
  { id: "thisQuarter", label: "هذا الربع" },
  { id: "lastQuarter", label: "الربع الماضي" },
  { id: "thisYear", label: "هذه السنة" },
  { id: "lastYear", label: "السنة الماضية" },
  { id: "custom", label: "فترة مخصصة" },
];

const TABS = [
  ["overview", "نظرة عامة"],
  ["profit", "الإيراد والربح"],
  ["production", "الإنتاج والتكلفة"],
  ["marketing", "التسويق"],
  ["returns", "المرتجعات والاستبدال"],
  ["deposits", "العربون والتحصيل"],
  ["expenses", "المصاريف"],
  ["reports", "التقارير"],
  ["settings", "الإعدادات"],
];

const DRILL = {
  netProductRevenue: "revenue",
  advertising: "advertising",
  cogs: "cogs",
  replacement: "replacements",
  overhead: "overhead",
  grossProfit: "revenue",
};

function rangeFor(preset, customFrom, customTo) {
  const now = new Date();
  let from;
  let to;
  switch (preset) {
    case "lastMonth":
      from = startOfMonth(subMonths(now, 1));
      to = endOfMonth(from);
      break;
    case "thisQuarter":
      from = startOfQuarter(now);
      to = endOfQuarter(now);
      break;
    case "lastQuarter":
      from = startOfQuarter(subQuarters(now, 1));
      to = endOfQuarter(from);
      break;
    case "thisYear":
      from = startOfYear(now);
      to = endOfYear(now);
      break;
    case "lastYear":
      from = startOfYear(subYears(now, 1));
      to = endOfYear(from);
      break;
    case "custom":
      from = customFrom ? new Date(customFrom) : startOfMonth(now);
      to = customTo ? new Date(customTo) : endOfMonth(now);
      break;
    default:
      from = startOfMonth(now);
      to = endOfMonth(now);
  }
  return { startDate: formatISO(from), endDate: formatISO(to) };
}

function MoneyLine({ money }) {
  if (!money) return "—";
  return money.certainty === "NotApplicable" || (money.amount == null && money.certainty !== "Actual")
    ? formatMoney(money.amount, money.certainty)
    : formatMoney(money.amount, money.certainty);
}

function Kpi({ kpi, onOpen }) {
  const drill = DRILL[kpi.key];
  return (
    <button
      type="button"
      onClick={() => drill && onOpen(drill, kpi.title)}
      className="text-right bg-white rounded-2xl border border-slate-200 p-3 shadow-sm min-w-0"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-slate-500">{kpi.title}</p>
        <span className={`text-[11px] rounded-full px-2 py-0.5 ${certaintyClass(kpi.certainty)}`}>
          {certaintyLabel[kpi.certainty] || kpi.certainty}
        </span>
      </div>
      <p className="mt-2 text-lg font-bold text-slate-900 break-words">
        {kpi.unit === "count" ? formatCount(kpi.amount) : kpi.key.includes("Margin") || kpi.key.includes("margin") ? formatPercent(kpi.amount) : formatMoney(kpi.amount, kpi.certainty)}
      </p>
      <p className="mt-1 text-[11px] leading-5 text-slate-500 line-clamp-3">{kpi.definition}</p>
    </button>
  );
}

const E7seblyPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authState } = useAuth();
  const [preset, setPreset] = useState("thisMonth");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [compare, setCompare] = useState("");
  const [tab, setTab] = useState("overview");
  const [drill, setDrill] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!authState.token && !authState.isValidating) navigate("/login");
    else if (!authState.isValidating && !canViewFinancials(authState.role)) navigate("/dashboard");
  }, [authState, navigate]);

  const filters = useMemo(() => ({ ...rangeFor(preset, customFrom, customTo), compare: compare || undefined }), [preset, customFrom, customTo, compare]);
  const preview = useQuery({
    queryKey: ["e7sebly", filters],
    queryFn: () => fetchE7seblyPreview(filters),
    enabled: canViewFinancials(authState.role),
  });
  const settings = useQuery({
    queryKey: ["e7sebly-settings"],
    queryFn: fetchE7seblySettings,
    enabled: canViewFinancials(authState.role) && tab === "settings",
  });
  const reports = useQuery({
    queryKey: ["e7sebly-reports"],
    queryFn: fetchE7seblyReports,
    enabled: canViewFinancials(authState.role) && tab === "reports",
  });
  const detail = useQuery({
    queryKey: ["e7sebly-drill", filters.startDate, filters.endDate, drill?.metric],
    queryFn: () => fetchE7seblyDrilldown(filters, drill.metric),
    enabled: !!drill && canViewFinancials(authState.role),
  });

  if (!canViewFinancials(authState.role)) return null;
  const statement = preview.data?.statement;
  const inventory = preview.data?.inventory;

  const saveReport = async (finalize) => {
    setError("");
    try {
      await saveE7seblyReport({ startDate: filters.startDate, endDate: filters.endDate, finalize, note: finalize ? "اعتماد الفترة" : "حفظ الحساب" });
      setMessage(finalize ? "تم اعتماد التقرير. التعديلات اللاحقة على الإعدادات لا تغيّره." : "تم حفظ لقطة الحساب.");
      queryClient.invalidateQueries({ queryKey: ["e7sebly-reports"] });
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div dir="rtl" className="min-h-screen bg-slate-100 p-3 md:p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => navigate("/dashboard")} className="p-2 rounded-xl bg-white border border-slate-200">
              <ChevronLeft size={18} />
            </button>
            <div className="p-2 rounded-xl bg-slate-900 text-white"><Calculator size={18} /></div>
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-slate-900">احسبلي</h1>
              <p className="text-sm text-slate-500">حساب تشغيلي تقديري. الأرقام الناقصة لا تُعامل كصفر.</p>
            </div>
          </div>
          <Link to="/e7sebly/inventory" className="rounded-xl bg-white border border-slate-200 px-3 py-2 text-sm font-medium">
            قيمة المخزون
          </Link>
        </div>

        <SectionCard title="الفترة" subtitle="الإيراد يُحسب بتاريخ التسليم. الطلبات تُحسب بتاريخ الإنشاء. العربون يُحسب بتاريخ تسجيله. هذه التواريخ ليست نفس الشيء.">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((item) => (
              <button key={item.id} type="button" onClick={() => setPreset(item.id)} className={`px-3 py-1.5 rounded-full text-sm ${preset === item.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>
                {item.label}
              </button>
            ))}
          </div>
          {preset === "custom" && (
            <div className="mt-3 flex flex-wrap gap-2">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="border rounded-lg px-2 py-1" />
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="border rounded-lg px-2 py-1" />
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <select value={compare} onChange={(e) => setCompare(e.target.value)} className="border rounded-lg px-2 py-1 text-sm">
              <option value="">بدون مقارنة</option>
              <option value="previous">الفترة السابقة بنفس المدة</option>
              <option value="year">نفس الفترة من السنة الماضية</option>
            </select>
            <button type="button" onClick={() => saveReport(false)} className="px-3 py-1.5 rounded-lg bg-white border text-sm">حفظ اللقطة</button>
            <button type="button" onClick={() => saveReport(true)} className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm">اعتماد الفترة</button>
          </div>
          {message && <p className="mt-2 text-sm text-emerald-700">{message}</p>}
          {error && <p className="mt-2 text-sm text-rose-700">{error}</p>}
        </SectionCard>

        <div className="flex gap-2 overflow-x-auto pb-1">
          {TABS.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setTab(id)} className={`shrink-0 px-3 py-2 rounded-xl text-sm ${tab === id ? "bg-white shadow-sm font-semibold" : "text-slate-500"}`}>
              {label}
            </button>
          ))}
        </div>

        {preview.isLoading && <LoadingBlock />}
        {preview.error && <EmptyState message={preview.error.message} />}
        {statement && tab !== "settings" && tab !== "reports" && (
          <>
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
              {statement.resultKind === "ProvisionalOperatingEstimate"
                ? "النتيجة تقدير تشغيلي لأن بعض التكاليف قياسية أو ناقصة. قيمة المخزون أصل وليست مصروفاً."
                : "المدخلات المسجّلة مكتملة لهذه المعادلة، مع بقاء تكلفة البضاعة قياسية وليست فواتير شراء."}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {statement.kpis.map((kpi) => <Kpi key={kpi.key} kpi={kpi} onOpen={(metric, title) => setDrill({ metric, title })} />)}
            </div>
            {inventory && (
              <SectionCard title="المخزون الآن" subtitle="أصل، لا يُخصم من الربح." action={<Link to="/e7sebly/inventory" className="text-sm text-indigo-700">فتح قيمة المخزون</Link>}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><p className="text-slate-500">الكمية المتاحة</p><p className="font-bold">{formatCount(inventory.totalAvailableQuantity)}</p></div>
                  <div><p className="text-slate-500">قيمة المخزون</p><p className="font-bold">{formatMoney(inventory.totalValue?.amount, inventory.totalValue?.certainty)}</p></div>
                  <div><p className="text-slate-500">الكفرات</p><p className="font-bold">{formatMoney(inventory.caseValue?.amount, inventory.caseValue?.certainty)}</p></div>
                  <div><p className="text-slate-500">المسترجع القابل للاستخدام</p><p className="font-bold">{formatMoney(inventory.recoveredValue?.amount, inventory.recoveredValue?.certainty)}</p></div>
                </div>
              </SectionCard>
            )}
          </>
        )}

        {statement && tab === "overview" && (
          <SectionCard title="الإيراد مقابل التكاليف" subtitle="مجموع النقاط اليومية قد يختلف بقرش عن إجمالي الفترة بسبب التقريب. الإجمالي في البطاقات هو المعتمد.">
            <ChartBox>
              <LineChart data={statement.series || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="netProductRevenue" name="صافي الإيراد" stroke="#0f766e" dot={false} />
                <Line type="monotone" dataKey="cogs" name="تكلفة البضاعة" stroke="#b45309" dot={false} />
                <Line type="monotone" dataKey="netOperatingProfit" name="الربح التشغيلي" stroke="#1d4ed8" dot={false} />
              </LineChart>
            </ChartBox>
            {preview.data?.comparison && (
              <p className="mt-3 text-sm text-slate-600">
                {preview.data.comparisonLabel}: صافي الربح {formatMoney(preview.data.comparison.netOperatingProfit?.amount, preview.data.comparison.netOperatingProfit?.certainty)}
              </p>
            )}
          </SectionCard>
        )}

        {statement && tab === "profit" && (
          <SectionCard title="قائمة الدخل" subtitle={statement.revenuePolicy}>
            <FormulaRows rows={[
              ["صافي إيراد المنتج", statement.netProductRevenue],
              ["إيراد الشحن من العميل", statement.shippingRevenue],
              ["تكلفة البضاعة", statement.cogs],
              ["مجمل الربح", statement.grossProfit],
              ["هامش مجمل الربح", statement.grossMargin, true],
              ["الإعلان", statement.advertising],
              ["الشحن والتغليف", statement.fulfillment],
              ["تكلفة الاستبدال الإضافية", statement.replacementCost],
              ["المساهمة بعد التكاليف المتغيرة", statement.contribution],
              ["المصاريف التشغيلية", statement.overhead],
              ["دخل الاستبدال المحصّل", statement.otherIncome],
              ["صافي الربح التشغيلي", statement.netOperatingProfit],
              ["هامش صافي الربح", statement.netMargin, true],
            ]} />
          </SectionCard>
        )}

        {statement && tab === "production" && (
          <SectionCard title="التكلفة حسب نوع المنتج" subtitle={statement.cogsPolicy}>
            <p className="text-sm mb-3">قطع مطبوعة ولم تُسلَّم: {formatCount(statement.printedNotSoldPieces)}. لا تُحسب هالكاً ولا مبيعات.</p>
            <p className="text-sm mb-3">الحبر: {MoneyLine({ money: statement.inkCost })} · هامش الهالك المتوقع (غير مضاف مرتين): {MoneyLine({ money: statement.expectedWaste })}</p>
            <ProductTable rows={statement.productTypes} />
          </SectionCard>
        )}

        {statement && tab === "marketing" && (
          <SectionCard title="الإنفاق حسب مصدر الإسناد" subtitle={statement.attributionPolicy}>
            <ChartBox className="h-64">
              <BarChart data={(statement.channels || []).filter((c) => c.advertisingSpend != null)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="advertisingSpend" name="الإنفاق" fill="#0f172a" />
              </BarChart>
            </ChartBox>
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm">
                <thead><tr className="text-slate-500"><th className="p-2 text-right">المصدر</th><th>طلبات</th><th>قطع</th><th>إنفاق</th><th>CPP المنصة</th><th>تكلفة الطلب الداخلية</th><th>تكلفة القطعة</th></tr></thead>
                <tbody>
                  {(statement.channels || []).map((row) => (
                    <tr key={row.channel} className="border-t">
                      <td className="p-2">{row.label}</td>
                      <td>{formatCount(row.ordersCreated)}</td>
                      <td>{formatCount(row.piecesCreated)}</td>
                      <td>{formatMoney(row.advertisingSpend, row.spendCertainty)}</td>
                      <td>{formatMoney(row.platformReportedCpp, row.platformReportedCpp == null ? "NotApplicable" : "Estimated")}</td>
                      <td>{formatMoney(row.internalCostPerOrder, row.internalCostPerOrder == null ? "NotApplicable" : row.spendCertainty)}</td>
                      <td>{formatMoney(row.costPerPiece, row.costPerPiece == null ? "NotApplicable" : row.spendCertainty)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
        )}

        {statement && tab === "returns" && (
          <SectionCard title="المرتجعات والاستبدال" subtitle={statement.internalReturnRateDenominator}>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <p>نسبة المرتجع الداخلية: {formatPercent(statement.internalReturnRate)}</p>
              <p>نسبة شركة الشحن: {statement.carrierReturnRate == null ? "غير مسجّلة" : `${statement.carrierReturnRate}% · ${statement.carrierReturnRateSource || ""}`}</p>
              <p>تعريف نسبة الشركة: {statement.carrierReturnRateDefinition || "لا تُجمع مع النسبة الداخلية."}</p>
              <p>طلبات الاستبدال: {formatCount(statement.replacementOrders)} · القطع: {formatCount(statement.replacementPieces)} · المطبوع: {formatCount(statement.replacementPiecesPrinted)} · المكتمل: {formatCount(statement.completedReplacements)}</p>
              <p>نسبة الاستبدال: {formatPercent(statement.replacementRate)}. {statement.replacementRateDenominator}</p>
              <p>تكلفة الاستبدال الإضافية: {MoneyLine({ money: statement.replacementCost })}</p>
              <p>توفير إعادة استخدام الكفرات (مذكرة، ليست إيراداً): {MoneyLine({ money: statement.recoveredReuseMemo })}</p>
            </div>
          </SectionCard>
        )}

        {statement && tab === "deposits" && (
          <SectionCard title="العربون" subtitle={statement.depositPolicy}>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <p>طلبات تتطلب عربوناً: {formatCount(statement.depositRequiredOrders)}</p>
              <p>مؤكدة وفيها عربون مدفوع: {formatCount(statement.confirmedWithDeposit)}</p>
              <p>نصيب العربون من المؤكد: {formatPercent(statement.depositShareOfConfirmed)}. {statement.depositShareOfConfirmedDenominator}</p>
              <p>نصيب العربون من طلبات فئة العربون: {formatPercent(statement.depositShareOfEligible)}. {statement.depositShareOfEligibleDenominator}</p>
              <p>المحصّل خلال الفترة: {MoneyLine({ money: statement.depositsCollected })}</p>
              <p>المتبقي: {MoneyLine({ money: statement.outstandingBalances })}</p>
              <p>زيادة التحصيل: {MoneyLine({ money: statement.overpayments })}</p>
              <p>مدفوعات أونلاين: {MoneyLine({ money: statement.onlinePayments })}</p>
              <p>دعم شحن جربات: {MoneyLine({ money: statement.shippingSubsidy })}</p>
            </div>
          </SectionCard>
        )}

        {statement && tab === "expenses" && (
          <SectionCard title="المصاريف المطبقة على الفترة" subtitle={statement.overhead?.definition}>
            <ul className="space-y-2 text-sm">
              {(statement.overheadRows || []).map((row) => (
                <li key={row.name} className="flex justify-between gap-3 border-b py-2">
                  <span>{row.name}<span className="block text-slate-500">{row.detail}</span></span>
                  <span>{formatMoney(row.amount, row.certainty)}</span>
                </li>
              ))}
              {(statement.overheadRows || []).length === 0 && <li>لا توجد مصاريف مؤكدة لهذه الفترة.</li>}
            </ul>
          </SectionCard>
        )}

        {statement && (
          <SectionCard title="حدود الدقة">
            <ul className="list-disc pr-5 text-sm text-slate-600 space-y-1">
              {(statement.limitations || []).map((item) => <li key={item}>{item}</li>)}
            </ul>
          </SectionCard>
        )}

        {tab === "reports" && (
          <ReportsPanel reports={reports.data || []} onRevise={async (id, note) => {
            await reviseE7seblyReport(id, { note, finalize: false });
            queryClient.invalidateQueries({ queryKey: ["e7sebly-reports"] });
          }} />
        )}

        {tab === "settings" && <SettingsPanel settings={settings.data} onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ["e7sebly"] });
          queryClient.invalidateQueries({ queryKey: ["e7sebly-settings"] });
          queryClient.invalidateQueries({ queryKey: ["e7sebly-inventory"] });
        }} />}

        {drill && (
          <SectionCard title={drill.title} subtitle={detail.data?.definition} action={<button type="button" onClick={() => setDrill(null)} className="text-sm">إغلاق</button>}>
            {detail.isLoading && <LoadingBlock />}
            <ul className="space-y-2 text-sm">
              {(detail.data?.rows || []).map((row, index) => (
                <li key={`${row.orderId}-${index}`} className="border-b py-2">
                  <div className="flex justify-between gap-3">
                    <span>{row.orderId ? <Link className="text-indigo-700" to={`/search-orders?orderId=${row.orderId}`}>{row.title}</Link> : row.title}</span>
                    <span>{formatMoney(row.amount, row.certainty)}</span>
                  </div>
                  <p className="text-slate-500">{row.detail}</p>
                </li>
              ))}
              {!detail.isLoading && (detail.data?.rows || []).length === 0 && <EmptyState message="لا توجد حركة مفصّلة" />}
            </ul>
          </SectionCard>
        )}
      </div>
    </div>
  );
};

function FormulaRows({ rows }) {
  return (
    <div className="space-y-2 text-sm">
      {rows.map(([label, money, percent]) => (
        <div key={label} className="flex justify-between gap-3 border-b py-2">
          <span>{label}</span>
          <span className="text-left">{percent ? formatPercent(money?.amount) : formatMoney(money?.amount, money?.certainty)}</span>
        </div>
      ))}
    </div>
  );
}

function ProductTable({ rows }) {
  if (!rows?.length) return <EmptyState message="لا توجد مبيعات مصنفة لهذه الفترة" />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-slate-500"><th className="p-2 text-right">النوع</th><th>مباع</th><th>تكلفة البضاعة</th><th>هالك متوقع</th><th>اليقين</th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.kind}-${row.name}`} className="border-t">
              <td className="p-2">{row.name}</td>
              <td>{formatCount(row.soldPieces)}</td>
              <td>{formatMoney(row.cogs, row.certainty)}</td>
              <td>{formatMoney(row.expectedWaste, "Estimated")}</td>
              <td>{certaintyLabel[row.certainty]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportsPanel({ reports, onRevise }) {
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState("");
  const detail = useQuery({
    queryKey: ["e7sebly-report", openId],
    queryFn: () => fetchE7seblyReport(openId),
    enabled: !!openId,
  });
  return (
    <SectionCard title="التقارير المحفوظة" subtitle="اللقطة المحفوظة لا تتغير عند تعديل إعدادات اليوم.">
      <ul className="space-y-2 text-sm">
        {reports.map((report) => (
          <li key={report.id} className="border rounded-xl p-3">
            <button type="button" className="w-full text-right" onClick={() => setOpenId(report.id)}>
              <span className="font-semibold">{report.periodStart?.slice(0, 10)} → {report.periodEnd?.slice(0, 10)}</span>
              <span className="mx-2">{report.status}</span>
              <span>مراجعة {report.revision}</span>
              <span className="block">{formatMoney(report.netOperatingProfit, report.profitCertainty)}</span>
            </button>
          </li>
        ))}
        {reports.length === 0 && <EmptyState message="لا توجد تقارير محفوظة" />}
      </ul>
      {detail.data?.statement && (
        <div className="mt-4 text-sm">
          <p>صافي الإيراد المحفوظ: {formatMoney(detail.data.statement.netProductRevenue?.amount, detail.data.statement.netProductRevenue?.certainty)}</p>
          <p>الربح المحفوظ: {formatMoney(detail.data.statement.netOperatingProfit?.amount, detail.data.statement.netOperatingProfit?.certainty)}</p>
          <div className="mt-2 flex gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="سبب إعادة الحساب" className="border rounded-lg px-2 py-1 flex-1" />
            <button type="button" className="bg-slate-900 text-white rounded-lg px-3" onClick={() => note.trim() && onRevise(openId, note.trim())}>إعادة الحساب</button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function SettingsPanel({ settings, onSaved }) {
  const [formError, setFormError] = useState("");
  if (!settings) return <LoadingBlock />;
  const save = async (action) => {
    setFormError("");
    try {
      await action();
      onSaved();
    } catch (err) {
      setFormError(err.message);
    }
  };
  return (
    <div className="space-y-4">
      {formError && <p className="text-sm text-rose-700">{formError}</p>}
      <SectionCard title="المصاريف التشغيلية" subtitle={settings.expenseAllocationPolicy}>
        {settings.expenses.filter((row) => !row.effectiveTo).map((row) => (
          <ExpenseForm key={row.id} row={row} onSave={(body) => save(() => saveE7seblyExpense(body))} />
        ))}
        <ExpenseForm row={{ name: "", category: "Other", amount: 0, recurrence: "Monthly", isActive: true, isConfigured: false }} onSave={(body) => save(() => saveE7seblyExpense(body))} />
      </SectionCard>
      <SectionCard title="تكلفة الأنواع" subtitle="لا توجد تكلفة عامة لكل الكفرات. النوع بدون تكلفة يبقى ناقصاً.">
        <ProductCostForm catalog={settings.catalog} onSave={(body) => save(() => saveE7seblyProductCost(body))} />
        <ul className="mt-3 text-sm space-y-1">
          {settings.productCosts.filter((row) => !row.effectiveTo).map((row) => (
            <li key={row.id}>{row.displayName}: {row.wholesaleConfigured ? `${row.wholesaleCost} ج.م` : "غير مكوّنة"} · هالك {row.wasteAllowanceConfigured ? row.wasteAllowanceRate : "غير مكوّن"}</li>
          ))}
        </ul>
      </SectionCard>
      <SectionCard title="الحبر والتغليف ودعم الشحن" subtitle={settings.inkWastePolicy}>
        {settings.consumables.filter((row) => !row.effectiveTo).map((row) => (
          <ConsumableForm key={row.id} row={row} onSave={(body) => save(() => saveE7seblyConsumable(body))} />
        ))}
        {settings.subsidies.filter((row) => !row.effectiveTo).map((row) => (
          <SubsidyForm key={row.id} row={row} onSave={(body) => save(() => saveE7seblySubsidy(body))} />
        ))}
      </SectionCard>
      <SectionCard title="إنفاق إعلاني يدوي ونسبة مرتجع شركة الشحن" subtitle="إنفاق ميتا المتزامن لا يُجمع مع إدخال يدوي لنفس الفترة. نسبة الشركة للمقارنة فقط.">
        <AdSpendForm onSave={(body) => save(() => saveE7seblyAdSpend(body))} />
        <ReturnRateForm onSave={(body) => save(() => saveE7seblyReturnRate(body))} />
      </SectionCard>
      <SectionCard title="تكلفة شحن فعلية لطلب" subtitle="تُستخدم بدل متوسط الدعم لهذا الطلب فقط. لا تُجمع مع التقدير.">
        <ShippingCostForm onSave={(body) => save(() => saveE7seblyShippingCost(body))} />
      </SectionCard>
      <SectionCard title="سجل التعديلات" subtitle="القيمة السابقة، القيمة الجديدة، وتاريخ السريان.">
        {(settings.changes || []).length === 0 ? <EmptyState message="لا توجد تعديلات محفوظة" /> : (
          <ul className="text-sm space-y-2">
            {settings.changes.map((row) => (
              <li key={row.id} className="border-b py-2">
                <span className="font-medium">{row.fieldName}</span>
                <span className="text-slate-500"> {row.previousValue ?? "—"} → {row.newValue ?? "—"}</span>
                <p className="text-slate-500">يسري من {String(row.effectiveFrom).slice(0, 10)} · {row.changedByEmail || "غير معروف"}</p>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block text-xs text-slate-500 mb-2">{label}{children}</label>;
}

function ExpenseForm({ row, onSave }) {
  const [amount, setAmount] = useState(row.amount ?? 0);
  const [name, setName] = useState(row.name || "");
  const [category, setCategory] = useState(row.category || "Other");
  const [recurrence, setRecurrence] = useState(row.recurrence || "Monthly");
  const [effectiveFrom, setEffectiveFrom] = useState((row.effectiveFrom || new Date().toISOString()).slice(0, 10));
  const [configured, setConfigured] = useState(!!row.isConfigured);
  const [active, setActive] = useState(row.isActive !== false);
  return (
    <form className="grid md:grid-cols-6 gap-2 items-end border-b py-3" onSubmit={(e) => {
      e.preventDefault();
      onSave({ id: row.id, name, category, amount: Number(amount), currency: "EGP", recurrence, effectiveFrom, isConfigured: configured, isActive: active, notes: configured ? "Confirmed by owner" : "Not configured" });
    }}>
      <Field label="الاسم"><input className="w-full border rounded-lg px-2 py-1" value={name} onChange={(e) => setName(e.target.value)} required /></Field>
      <Field label="التصنيف"><input className="w-full border rounded-lg px-2 py-1" value={category} onChange={(e) => setCategory(e.target.value)} required /></Field>
      <Field label="المبلغ"><input type="number" min="0" step="0.01" className="w-full border rounded-lg px-2 py-1" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
      <Field label="التكرار"><select className="w-full border rounded-lg px-2 py-1" value={recurrence} onChange={(e) => setRecurrence(e.target.value)}><option value="Monthly">شهري</option><option value="Yearly">سنوي</option><option value="OneTime">مرة واحدة</option></select></Field>
      <Field label="يسري من"><input type="date" className="w-full border rounded-lg px-2 py-1" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} required /></Field>
      <div>
        <label className="text-xs flex gap-1"><input type="checkbox" checked={configured} onChange={(e) => setConfigured(e.target.checked)} />مؤكد، والصفر صفر حقيقي</label>
        <label className="text-xs flex gap-1"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />نشط</label>
        <button className="mt-1 w-full bg-slate-900 text-white rounded-lg py-1 text-sm" type="submit">حفظ التغييرات</button>
      </div>
    </form>
  );
}

function ProductCostForm({ catalog = [], onSave }) {
  const [target, setTarget] = useState(catalog[0] ? `${catalog[0].productKind}:${catalog[0].targetId}` : "");
  const [cost, setCost] = useState("");
  const [waste, setWaste] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const selected = catalog.find((item) => `${item.productKind}:${item.targetId}` === target);
  return (
    <form className="grid md:grid-cols-5 gap-2 items-end" onSubmit={(e) => {
      e.preventDefault();
      if (!selected) return;
      onSave({
        productKind: selected.productKind,
        targetId: selected.targetId,
        displayName: selected.displayName,
        wholesaleCost: cost === "" ? null : Number(cost),
        wholesaleConfigured: cost !== "",
        wasteAllowanceRate: waste === "" ? null : Number(waste),
        wasteAllowanceConfigured: waste !== "",
        effectiveFrom,
        isActive: true,
      });
    }}>
      <Field label="المنتج"><select className="w-full border rounded-lg px-2 py-1" value={target} onChange={(e) => setTarget(e.target.value)}>{catalog.map((item) => <option key={`${item.productKind}:${item.targetId}`} value={`${item.productKind}:${item.targetId}`}>{item.displayName}</option>)}</select></Field>
      <Field label="تكلفة الجملة"><input type="number" min="0" step="0.01" className="w-full border rounded-lg px-2 py-1" value={cost} onChange={(e) => setCost(e.target.value)} /></Field>
      <Field label="هالك متوقع (0.05 = 5%)"><input type="number" min="0" max="0.99" step="0.001" className="w-full border rounded-lg px-2 py-1" value={waste} onChange={(e) => setWaste(e.target.value)} /></Field>
      <Field label="يسري من"><input type="date" className="w-full border rounded-lg px-2 py-1" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} required /></Field>
      <button className="bg-slate-900 text-white rounded-lg py-2 text-sm" type="submit">حفظ التغييرات</button>
    </form>
  );
}

function ConsumableForm({ row, onSave }) {
  const [amount, setAmount] = useState(row.amount ?? "");
  const [text, setText] = useState(row.textValue || "OfBaseConsumption");
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const method = row.settingKey === "InkWasteMethod";
  return (
    <form className="grid md:grid-cols-4 gap-2 items-end py-2" onSubmit={(e) => {
      e.preventDefault();
      onSave({ settingKey: row.settingKey, amount: method ? null : Number(amount), textValue: method ? text : null, isConfigured: true, effectiveFrom });
    }}>
      <Field label={row.settingKey}>{method ? <select className="w-full border rounded-lg px-2 py-1" value={text} onChange={(e) => setText(e.target.value)}><option value="OfBaseConsumption">من الاستهلاك الأساسي</option><option value="OfTotalConsumption">من إجمالي الحبر</option></select> : <input type="number" min="0" step="0.01" className="w-full border rounded-lg px-2 py-1" value={amount} onChange={(e) => setAmount(e.target.value)} required />}</Field>
      <Field label="يسري من"><input type="date" className="w-full border rounded-lg px-2 py-1" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} required /></Field>
      <p className="text-xs text-slate-500">{row.isConfigured ? "مؤكد" : "غير مكوّن"}</p>
      <button className="bg-slate-900 text-white rounded-lg py-2 text-sm" type="submit">حفظ التغييرات</button>
    </form>
  );
}

function SubsidyForm({ row, onSave }) {
  const [amount, setAmount] = useState(row.amountPerOrder ?? 15);
  const [provider, setProvider] = useState(row.shippingProvider || "");
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  return (
    <form className="grid md:grid-cols-4 gap-2 items-end py-2" onSubmit={(e) => {
      e.preventDefault();
      onSave({ id: row.id, shippingProvider: provider || null, amountPerOrder: Number(amount), isConfigured: true, isActive: true, effectiveFrom, notes: "Configured estimate. Actual carrier costs replace it per order." });
    }}>
      <Field label="متوسط دعم الشحن"><input type="number" min="0" step="0.01" className="w-full border rounded-lg px-2 py-1" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
      <Field label="شركة الشحن (فارغ = الافتراضي)"><input className="w-full border rounded-lg px-2 py-1" value={provider} onChange={(e) => setProvider(e.target.value)} /></Field>
      <Field label="يسري من"><input type="date" className="w-full border rounded-lg px-2 py-1" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} required /></Field>
      <button className="bg-slate-900 text-white rounded-lg py-2 text-sm" type="submit">حفظ التغييرات</button>
    </form>
  );
}

function AdSpendForm({ onSave }) {
  const [platform, setPlatform] = useState("TikTok");
  const [spend, setSpend] = useState("");
  const [cpp, setCpp] = useState("");
  const [purchases, setPurchases] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [verified, setVerified] = useState(false);
  return (
    <form className="grid md:grid-cols-4 gap-2 items-end" onSubmit={(e) => {
      e.preventDefault();
      onSave({ platform, periodStart: start, periodEnd: end, actualSpend: spend === "" ? null : Number(spend), spendVerified: verified, reportedCpp: cpp === "" ? null : Number(cpp), platformReportedPurchases: purchases === "" ? null : Number(purchases) });
    }}>
      <Field label="المنصة"><select className="w-full border rounded-lg px-2 py-1" value={platform} onChange={(e) => setPlatform(e.target.value)}><option>TikTok</option><option>GoogleAds</option><option>Meta</option></select></Field>
      <Field label="إنفاق فعلي"><input type="number" min="0" step="0.01" className="w-full border rounded-lg px-2 py-1" value={spend} onChange={(e) => setSpend(e.target.value)} /></Field>
      <Field label="CPP المنصة"><input type="number" min="0" step="0.01" className="w-full border rounded-lg px-2 py-1" value={cpp} onChange={(e) => setCpp(e.target.value)} /></Field>
      <Field label="مشتريات المنصة"><input type="number" min="0" className="w-full border rounded-lg px-2 py-1" value={purchases} onChange={(e) => setPurchases(e.target.value)} /></Field>
      <Field label="من"><input type="date" required className="w-full border rounded-lg px-2 py-1" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
      <Field label="إلى"><input type="date" required className="w-full border rounded-lg px-2 py-1" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
      <label className="text-xs"><input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} /> إنفاق مؤكد</label>
      <button className="bg-slate-900 text-white rounded-lg py-2 text-sm" type="submit">حفظ التغييرات</button>
    </form>
  );
}

function ShippingCostForm({ onSave }) {
  const [orderId, setOrderId] = useState("");
  const [kind, setKind] = useState("Outbound");
  const [cost, setCost] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  return (
    <form className="grid md:grid-cols-5 gap-2 items-end" onSubmit={(e) => {
      e.preventDefault();
      onSave({ orderId: Number(orderId), kind, actualCarrierCost: Number(cost), costDate: date, notes: "Actual carrier cost" });
    }}>
      <Field label="رقم الطلب"><input type="number" min="1" required className="w-full border rounded-lg px-2 py-1" value={orderId} onChange={(e) => setOrderId(e.target.value)} /></Field>
      <Field label="النوع"><select className="w-full border rounded-lg px-2 py-1" value={kind} onChange={(e) => setKind(e.target.value)}><option value="Outbound">شحن صادر</option><option value="Return">مرتجع</option><option value="FailedDelivery">فشل تسليم</option><option value="Replacement">استبدال</option></select></Field>
      <Field label="تكلفة الشركة"><input type="number" min="0" step="0.01" required className="w-full border rounded-lg px-2 py-1" value={cost} onChange={(e) => setCost(e.target.value)} /></Field>
      <Field label="تاريخ التكلفة"><input type="date" required className="w-full border rounded-lg px-2 py-1" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
      <button className="bg-slate-900 text-white rounded-lg py-2 text-sm" type="submit">حفظ التغييرات</button>
    </form>
  );
}

function ReturnRateForm({ onSave }) {
  const [rate, setRate] = useState("");
  const [source, setSource] = useState("");
  const [definition, setDefinition] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  return (
    <form className="grid md:grid-cols-3 gap-2 items-end mt-4" onSubmit={(e) => {
      e.preventDefault();
      onSave({ periodStart: start, periodEnd: end, carrierReturnRatePercent: Number(rate), source, denominatorDefinition: definition });
    }}>
      <Field label="نسبة شركة الشحن %"><input type="number" min="0" max="100" step="0.01" required className="w-full border rounded-lg px-2 py-1" value={rate} onChange={(e) => setRate(e.target.value)} /></Field>
      <Field label="المصدر"><input required className="w-full border rounded-lg px-2 py-1" value={source} onChange={(e) => setSource(e.target.value)} /></Field>
      <Field label="تعريف المقام"><input required className="w-full border rounded-lg px-2 py-1" value={definition} onChange={(e) => setDefinition(e.target.value)} /></Field>
      <Field label="من"><input type="date" required className="w-full border rounded-lg px-2 py-1" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
      <Field label="إلى"><input type="date" required className="w-full border rounded-lg px-2 py-1" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
      <button className="bg-slate-900 text-white rounded-lg py-2 text-sm" type="submit">حفظ التغييرات</button>
    </form>
  );
}

export default E7seblyPage;
